import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getLinkedFirm } from "../../../../../sessionStorage/sessionStorage";
import {
  useAiTemplateDraftMutation,
  useGetTemplateDraftOptionsQuery,
  useSaveTemplateDraftMutation,
} from "../../../../../slices/TemplateDraftApi";
import { aiErrorMessage } from "../../../../ai/ai.utils";
import {
  modalCancel,
  modalPrimary,
  PortalModal,
} from "../../../../clientPortal/PortalModal";
import {
  addRow,
  DraftMode,
  draftProblem,
  DraftRow,
  draftSummary,
  DueRule,
  dueRuleText,
  EditableDraft,
  EXAMPLES,
  fromServerDraft,
  MAX_ROWS,
  memberLabel,
  moveRow,
  removeRow,
  rowDays,
  toAddTemplatePayload,
  toServerDraft,
  updateRow,
} from "./templateDraft.utils";

const MODES: { id: DraftMode; title: string; hint: string }[] = [
  { id: "describe", title: "Describe it", hint: "Say what the work is." },
  {
    id: "paste",
    title: "Paste a checklist",
    hint: "Any list, email or notes.",
  },
  {
    id: "past_work",
    title: "Learn from past work",
    hint: "From your recent work items.",
  },
];

const label = "text-[12px] font-semibold text-[#3A4256]";
const input =
  "w-full rounded-[9px] border border-[#DCE2EA] bg-white px-3 py-2 text-[13.5px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";
const smallInput =
  "h-8 rounded-lg border border-[#DCE2EA] bg-white px-2 text-[13px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";
const iconBtn =
  "flex h-7 w-7 items-center justify-center rounded-md text-[13px] text-[#6B7185] hover:bg-[#F1F2F5] disabled:opacity-30";

type Props = {
  onClose: () => void;
  onSaved: (templateId: string) => void;
};

export const BuildWithAiModal: React.FC<Props> = ({ onClose, onSaved }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: options } = useGetTemplateDraftOptionsQuery(
    { orgId },
    { skip: !orgId },
  );
  const [buildDraft, { isLoading: isBuilding }] = useAiTemplateDraftMutation();
  const [saveDraft, { isLoading: isSaving }] = useSaveTemplateDraftMutation();

  const [mode, setMode] = useState<DraftMode>("describe");
  const [describeText, setDescribeText] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [workTypeId, setWorkTypeId] = useState("");
  const [includeDueDays, setIncludeDueDays] = useState(true);
  const [includeRoles, setIncludeRoles] = useState(true);
  const [draft, setDraft] = useState<EditableDraft | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [instruction, setInstruction] = useState("");
  const [openDetails, setOpenDetails] = useState<Set<string>>(new Set());
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const workTypes = options?.workTypes || [];
  const team = options?.team || [];
  const pickedType = workTypes.find((w) => w.id === workTypeId);

  const buildProblem = useMemo(() => {
    if (mode === "describe" && !describeText.trim())
      return "Describe the work first.";
    if (mode === "paste" && !pasteText.trim())
      return "Paste a checklist first.";
    if (mode === "past_work" && !workTypeId)
      return "Pick the work type to learn from.";
    if (mode === "past_work" && pickedType && pickedType.pastWorkItems === 0)
      return "This work type has no past work with subtasks yet.";
    return null;
  }, [describeText, mode, pasteText, pickedType, workTypeId]);

  const close = () => {
    if (
      draft &&
      !window.confirm("Discard this template? Nothing has been saved.")
    ) {
      return;
    }
    onClose();
  };

  const run = async (refine: boolean) => {
    setError("");
    try {
      const result = await buildDraft({
        orgId,
        mode,
        text:
          mode === "describe"
            ? describeText.trim()
            : mode === "paste"
            ? pasteText.trim()
            : undefined,
        workTypeId: workTypeId || null,
        includeDueDays,
        includeRoles,
        ...(refine && draft
          ? {
              currentDraft: toServerDraft(draft),
              instruction: instruction.trim(),
            }
          : {}),
      }).unwrap();
      setDraft(fromServerDraft(result));
      setWarnings(result.warnings || []);
      setOpenDetails(new Set());
      if (refine) setInstruction("");
    } catch (e) {
      setError(aiErrorMessage(e));
    }
  };

  const setRows = (fn: (rows: DraftRow[]) => DraftRow[]) =>
    setDraft((d) => (d ? { ...d, rows: fn(d.rows) } : d));

  const saveProblem = draft ? draftProblem(draft) : "Build a template first.";

  const save = async () => {
    if (!draft || saveProblem) return;
    setError("");
    try {
      const created = await saveDraft(
        toAddTemplatePayload(draft, orgId),
      ).unwrap();
      toast("Template saved.", { type: "success" });
      onSaved(created.id);
    } catch (e) {
      setError(aiErrorMessage(e));
    }
  };

  const footer = (
    <>
      {saveProblem && draft && (
        <span className={"mr-auto text-[12.5px] font-medium text-[#B54708]"}>
          {saveProblem}
        </span>
      )}
      <button type={"button"} className={modalCancel} onClick={close}>
        Discard
      </button>
      <button
        type={"button"}
        className={modalPrimary}
        disabled={!draft || !!saveProblem || isSaving || isBuilding}
        onClick={save}>
        {isSaving ? "Saving…" : "Save template"}
      </button>
    </>
  );

  return (
    <PortalModal
      title={"✨ Build a subtask template with AI"}
      subtitle={
        "Describe the work, paste a checklist or learn from past work. You can change everything before saving."
      }
      onClose={close}
      footer={footer}
      width={1100}>
      <div
        className={"grid grid-cols-1 gap-6 lg:grid-cols-[340px_minmax(0,1fr)]"}>
        {/* Left: how to start */}
        <div className={"flex flex-col gap-4"}>
          <div className={"flex flex-col gap-2"}>
            <span className={label}>Start from</span>
            {MODES.map((m) => (
              <button
                key={m.id}
                type={"button"}
                aria-pressed={mode === m.id}
                onClick={() => setMode(m.id)}
                className={`flex flex-col items-start rounded-xl border px-3 py-2.5 text-left ${
                  mode === m.id
                    ? "border-[#6B4EFF] bg-[#F4F1FF]"
                    : "border-[#DCE2EA] bg-white hover:bg-[#F7F8FA]"
                }`}>
                <span className={"text-[13.5px] font-bold"}>{m.title}</span>
                <span className={"text-[12px] text-[#6B7185]"}>{m.hint}</span>
              </button>
            ))}
          </div>

          {mode === "describe" && (
            <div className={"flex flex-col gap-2"}>
              <label className={label} htmlFor={"tpl-ai-describe"}>
                What is the work?
              </label>
              <textarea
                id={"tpl-ai-describe"}
                rows={4}
                maxLength={2000}
                className={input}
                placeholder={
                  "For example: T1 personal return for a family with rental income"
                }
                value={describeText}
                onChange={(e) => setDescribeText(e.target.value)}
              />
              <div className={"flex flex-wrap gap-1.5"}>
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    type={"button"}
                    onClick={() => setDescribeText(ex)}
                    className={
                      "rounded-full border border-[#DCE2EA] bg-white px-2.5 py-1 text-[12px] font-semibold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
                    }>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === "paste" && (
            <div className={"flex flex-col gap-2"}>
              <label className={label} htmlFor={"tpl-ai-paste"}>
                Paste your checklist, email or notes
              </label>
              <textarea
                id={"tpl-ai-paste"}
                rows={8}
                maxLength={20000}
                className={input}
                placeholder={
                  "1. Send engagement letter\n2. Collect slips\n3. Prepare return…"
                }
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
              />
            </div>
          )}

          <div className={"flex flex-col gap-2"}>
            <label className={label} htmlFor={"tpl-ai-worktype"}>
              Work type{mode === "past_work" ? "" : " (optional)"}
            </label>
            <select
              id={"tpl-ai-worktype"}
              className={input}
              value={workTypeId}
              onChange={(e) => setWorkTypeId(e.target.value)}>
              <option value={""}>
                {mode === "past_work" ? "Pick a work type" : "Any"}
              </option>
              {workTypes.map((w) => (
                <option
                  key={w.id}
                  value={w.id}
                  disabled={mode === "past_work" && w.pastWorkItems === 0}>
                  {mode === "past_work"
                    ? `${w.name} (${
                        w.pastWorkItems === 0
                          ? "no past work"
                          : `${w.pastWorkItems} recent work item${
                              w.pastWorkItems === 1 ? "" : "s"
                            }`
                      })`
                    : w.name}
                </option>
              ))}
            </select>
            {mode === "past_work" && (
              <span className={"text-[12px] text-[#6B7185]"}>
                Reads the subtasks of up to 20 recent work items of this type
                and keeps the steps they have in common. Client names are never
                sent to the AI.
              </span>
            )}
          </div>

          <div className={"flex flex-col gap-2"}>
            <span className={label}>Include</span>
            <label className={"flex items-center gap-2 text-[13px]"}>
              <input
                type={"checkbox"}
                className={"accent-[#6B4EFF]"}
                checked={includeDueDays}
                onChange={(e) => setIncludeDueDays(e.target.checked)}
              />
              Due dates (days after start / before due)
            </label>
            <label className={"flex items-center gap-2 text-[13px]"}>
              <input
                type={"checkbox"}
                className={"accent-[#6B4EFF]"}
                checked={includeRoles}
                onChange={(e) => setIncludeRoles(e.target.checked)}
              />
              Assignees, picked by your team&apos;s roles
            </label>
          </div>

          <button
            type={"button"}
            className={`${modalPrimary} w-full`}
            disabled={!!buildProblem || isBuilding || !orgId}
            title={buildProblem || undefined}
            onClick={() => run(false)}>
            {isBuilding ? "Building…" : "✨ Build my template"}
          </button>
          <span className={"text-[12px] text-[#9097A8]"}>
            Uses your firm&apos;s own work types and roles. Nothing is saved
            until you click Save.
          </span>
        </div>

        {/* Right: the editable result */}
        <div className={"flex min-w-0 flex-col gap-3"}>
          {error && (
            <div
              role={"alert"}
              className={
                "rounded-xl border border-[#F5C2C0] bg-[#FEF3F2] px-3 py-2 text-[13px] text-[#B42318]"
              }>
              {error}
            </div>
          )}

          {!draft ? (
            <div
              className={
                "flex min-h-[260px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[#DCE2EA] bg-[#FAFBFC] px-6 text-center"
              }>
              <span className={"text-[15px] font-extrabold"}>
                {isBuilding
                  ? "Building your template…"
                  : "Your template will appear here"}
              </span>
              <span className={"max-w-[380px] text-[13px] text-[#6B7185]"}>
                {isBuilding
                  ? "This usually takes a few seconds."
                  : "Choose how to start on the left, then click “Build my template”. You can edit, reorder and delete every step."}
              </span>
            </div>
          ) : (
            <>
              <div className={"flex flex-col gap-1.5"}>
                <label className={label} htmlFor={"tpl-ai-name"}>
                  Template name
                </label>
                <input
                  id={"tpl-ai-name"}
                  className={input}
                  maxLength={100}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                <input
                  aria-label={"Description"}
                  className={`${input} text-[13px]`}
                  placeholder={"Description (optional)"}
                  value={draft.description}
                  onChange={(e) =>
                    setDraft({ ...draft, description: e.target.value })
                  }
                />
                <span className={"text-[12.5px] font-semibold text-[#6B7185]"}>
                  {draftSummary(draft.rows)}
                </span>
              </div>

              {warnings.length > 0 && (
                <ul
                  className={
                    "m-0 flex list-none flex-col gap-1 rounded-xl border border-[#FEDF89] bg-[#FFFAEB] px-3 py-2 text-[12.5px] text-[#93370D]"
                  }>
                  {warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}

              <ol
                className={
                  "m-0 flex list-none flex-col rounded-xl border border-[#EDEFF3] p-0"
                }>
                {draft.rows.map((row, index) => (
                  <DraftRowView
                    key={row.key}
                    row={row}
                    index={index}
                    count={draft.rows.length}
                    team={team}
                    detailsOpen={openDetails.has(row.key)}
                    isDragOver={dragIndex !== null && dragIndex !== index}
                    onToggleDetails={() =>
                      setOpenDetails((s) => {
                        const next = new Set(s);
                        if (next.has(row.key)) next.delete(row.key);
                        else next.add(row.key);
                        return next;
                      })
                    }
                    onChange={(patch) =>
                      setRows((rows) => updateRow(rows, row.key, patch))
                    }
                    onMove={(to) => setRows((rows) => moveRow(rows, index, to))}
                    onRemove={() => setRows((rows) => removeRow(rows, row.key))}
                    onDragStart={() => setDragIndex(index)}
                    onDragEnd={() => setDragIndex(null)}
                    onDrop={() => {
                      if (dragIndex !== null)
                        setRows((rows) => moveRow(rows, dragIndex, index));
                      setDragIndex(null);
                    }}
                  />
                ))}
              </ol>
              <button
                type={"button"}
                disabled={draft.rows.length >= MAX_ROWS}
                onClick={() => setRows(addRow)}
                className={
                  "self-start rounded-lg px-2 py-1 text-[13px] font-bold text-[#6B4EFF] hover:bg-[#F4F1FF] disabled:opacity-40"
                }>
                + Add subtask
              </button>

              <div
                className={
                  "flex flex-col gap-2 rounded-xl border border-[#EDEFF3] bg-[#FAFBFC] p-3"
                }>
                <label className={label} htmlFor={"tpl-ai-change"}>
                  Ask for changes
                </label>
                <div className={"flex gap-2"}>
                  <input
                    id={"tpl-ai-change"}
                    className={input}
                    maxLength={1000}
                    placeholder={
                      "For example: add a manager review step before sending"
                    }
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        instruction.trim() &&
                        !isBuilding
                      )
                        run(true);
                    }}
                  />
                  <button
                    type={"button"}
                    className={modalPrimary}
                    disabled={!instruction.trim() || isBuilding}
                    onClick={() => run(true)}>
                    {isBuilding ? "Updating…" : "Update"}
                  </button>
                </div>
                <span className={"text-[12px] text-[#9097A8]"}>
                  The AI changes your current version, including your own edits.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </PortalModal>
  );
};

const DUE_OPTIONS: { value: DueRule; text: string }[] = [
  { value: "none", text: "On the work's due date" },
  { value: "after_start", text: "Days after start" },
  { value: "before_due", text: "Days before due" },
];

const DraftRowView: React.FC<{
  row: DraftRow;
  index: number;
  count: number;
  team: { id: string; name: string; roles: string[] }[];
  detailsOpen: boolean;
  isDragOver: boolean;
  onToggleDetails: () => void;
  onChange: (patch: Partial<DraftRow>) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
}> = ({
  row,
  index,
  count,
  team,
  detailsOpen,
  isDragOver,
  onToggleDetails,
  onChange,
  onMove,
  onRemove,
  onDragStart,
  onDragEnd,
  onDrop,
}) => (
  <li
    onDragOver={(e) => isDragOver && e.preventDefault()}
    onDrop={(e) => {
      e.preventDefault();
      onDrop();
    }}
    className={
      "flex flex-col gap-2 border-b border-[#EDEFF3] px-3 py-2.5 last:border-b-0"
    }>
    <div className={"flex items-center gap-1.5"}>
      <span
        draggable
        onDragStart={(e) => {
          // Firefox only starts a drag that carries some data.
          e.dataTransfer.setData("text/plain", String(index));
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        title={"Drag to reorder"}
        className={"cursor-grab select-none px-1 text-[#9097A8]"}>
        ⋮⋮
      </span>
      <span className={"w-5 text-right text-[12px] font-bold text-[#9097A8]"}>
        {index + 1}
      </span>
      <input
        aria-label={`Subtask ${index + 1} title`}
        className={`${smallInput} min-w-0 flex-1 font-semibold`}
        maxLength={100}
        placeholder={"Subtask title"}
        value={row.title}
        onChange={(e) => onChange({ title: e.target.value })}
      />
      <button
        type={"button"}
        className={iconBtn}
        aria-label={"Move up"}
        disabled={index === 0}
        onClick={() => onMove(index - 1)}>
        ↑
      </button>
      <button
        type={"button"}
        className={iconBtn}
        aria-label={"Move down"}
        disabled={index === count - 1}
        onClick={() => onMove(index + 1)}>
        ↓
      </button>
      <button
        type={"button"}
        className={iconBtn}
        aria-label={"Delete subtask"}
        onClick={onRemove}>
        ✕
      </button>
    </div>
    <div className={"flex flex-wrap items-center gap-2 pl-[52px]"}>
      <select
        aria-label={"Due"}
        title={`Due: ${dueRuleText(row.dueRule, rowDays(row))}`}
        className={smallInput}
        value={row.dueRule}
        onChange={(e) =>
          onChange({
            dueRule: e.target.value as DueRule,
            days:
              e.target.value === "none" ? "" : row.days === "" ? "0" : row.days,
          })
        }>
        {DUE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.text}
          </option>
        ))}
      </select>
      {row.dueRule !== "none" && (
        <input
          aria-label={"Days"}
          type={"number"}
          min={0}
          max={365}
          inputMode={"numeric"}
          className={`${smallInput} w-[64px] text-right`}
          value={row.days}
          onChange={(e) => onChange({ days: e.target.value })}
        />
      )}
      <select
        aria-label={"Assignee"}
        className={`${smallInput} max-w-[220px]`}
        value={row.assignedUserId}
        onChange={(e) => onChange({ assignedUserId: e.target.value })}>
        <option value={""}>No assignee</option>
        {team.map((m) => (
          <option key={m.id} value={m.id}>
            {memberLabel(m)}
          </option>
        ))}
      </select>
      <span className={"flex items-center gap-1 text-[12.5px] text-[#6B7185]"}>
        <input
          aria-label={"Hours"}
          type={"number"}
          min={0}
          max={100}
          inputMode={"numeric"}
          className={`${smallInput} w-[56px] text-right`}
          placeholder={"0"}
          value={row.hours}
          onChange={(e) => onChange({ hours: e.target.value })}
        />
        h
      </span>
      <button
        type={"button"}
        onClick={onToggleDetails}
        className={
          "rounded-md px-1.5 py-0.5 text-[12.5px] font-semibold text-[#6B4EFF] hover:bg-[#F4F1FF]"
        }>
        {detailsOpen ? "Hide details" : row.details ? "Details" : "+ Details"}
      </button>
    </div>
    {detailsOpen && (
      <textarea
        aria-label={`Subtask ${index + 1} details`}
        rows={2}
        className={`${smallInput} ml-[52px] h-auto py-1.5`}
        placeholder={"What to check or attach (optional)"}
        value={row.details}
        onChange={(e) => onChange({ details: e.target.value })}
      />
    )}
  </li>
);
